import { Play, Pause, Volume2, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { usePlayer, formatTime } from "../context/AudioContext";

const EqBars = () => (
  <div className="flex items-end gap-[3px] h-4" aria-hidden="true">
    {[0, 1, 2, 3].map((i) => (
      <span
        key={i}
        className="eq-bar w-[3px] bg-gold rounded-full h-full"
        style={{ animationDelay: `${i * 0.15}s` }}
      />
    ))}
  </div>
);

export const AudioPlayerBar = () => {
  const { track, playing, currentTime, duration, volume, toggle, seek, setVolume, close } = usePlayer();

  return (
    <AnimatePresence>
      {track && (
        <motion.div
          initial={{ y: 90, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 90, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          data-testid="persistent-audio-player"
          className="fixed bottom-0 inset-x-0 z-50 bg-obsidian/90 backdrop-blur-lg border-t border-gold/25"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-8 h-[76px] flex items-center gap-3 sm:gap-5">
            {track.cover ? (
              <img src={track.cover} alt="" className="w-11 h-11 object-cover border border-gold/30 shrink-0" />
            ) : (
              <div className="w-11 h-11 bg-velvet border border-gold/30 shrink-0 flex items-center justify-center font-serif italic text-gold text-lg">Q</div>
            )}
            <div className="min-w-0 w-32 sm:w-48 shrink-0">
              <p data-testid="player-track-title" className="font-serif text-sm sm:text-base text-cream truncate">{track.title}</p>
              <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-gold/70 truncate">{track.tag}</p>
            </div>
            <button
              onClick={toggle}
              data-testid="play-pause-btn"
              aria-label={playing ? "Pause" : "Play"}
              className="w-11 h-11 rounded-full bg-gold text-obsidian flex items-center justify-center hover:bg-goldglow transition-colors shrink-0"
            >
              {playing ? <Pause size={17} /> : <Play size={17} className="ml-0.5" />}
            </button>
            <div className="hidden sm:block shrink-0">{playing ? <EqBars /> : <div className="h-4 w-[21px]" />}</div>
            <span className="hidden sm:block font-mono text-[10px] text-sand shrink-0 w-10 text-right">{formatTime(currentTime)}</span>
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={currentTime}
              onChange={(e) => seek(parseFloat(e.target.value))}
              data-testid="audio-slider"
              aria-label="Seek"
              className="gold-range flex-1 min-w-0"
            />
            <span className="hidden sm:block font-mono text-[10px] text-sand shrink-0 w-10">{formatTime(duration)}</span>
            <div className="hidden md:flex items-center gap-2 shrink-0">
              <Volume2 size={15} className="text-sand" />
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                data-testid="volume-slider"
                aria-label="Volume"
                className="gold-range w-20"
              />
            </div>
            <button
              onClick={close}
              data-testid="player-close-btn"
              aria-label="Close player"
              className="text-sand/60 hover:text-cream transition-colors shrink-0 p-1"
            >
              <X size={16} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
